import React, { useState } from 'react';
import { Globe, Phone, MessageSquare, TrendingUp, Users, Shield, Zap, Smartphone } from 'lucide-react';
import './Features.css';

function Features() {
  const features = [
    {
      icon: Globe,
      title: 'Custom Website',
      description: 'A beautiful, fast website built just for your business. Mobile-friendly, modern design that converts visitors into customers.',
      details: ['Responsive design', 'Fast loading speeds', 'SEO-ready', 'Professional branding']
    },
    {
      icon: TrendingUp,
      title: 'Local SEO',
      description: 'Get found by customers searching for your services in your area. We optimize everything so you rank higher on Google.',
      details: ['Google Business optimization', 'Local keyword targeting', 'Review management', 'Local citations']
    },
    {
      icon: Phone,
      title: 'AI Phone Assistant',
      description: 'Answer calls 24/7 with our intelligent phone system. It handles appointments, questions, and even takes payments.',
      details: ['24/7 availability', 'Appointment scheduling', 'Intelligent answering', 'Call transcripts']
    },
    {
      icon: Smartphone,
      title: 'Business Phone Line',
      description: 'A professional phone number for your business. Branded caller ID, call routing, and voicemail that works for you.',
      details: ['Professional number', 'Smart routing', 'Voicemail to email', 'Call analytics']
    },
    {
      icon: Users,
      title: 'Lead Capture & Forms',
      description: 'Contact forms, appointment booking, and quotes - all built into your website. Leads come straight to you.',
      details: ['Custom forms', 'Booking calendar', 'Quote requests', 'Auto-confirmations']
    },
    {
      icon: MessageSquare,
      title: 'Email & SMS Marketing',
      description: 'Stay in touch with your customers. Send promotions, updates, and reminders via email and text message.',
      details: ['Email campaigns', 'SMS blasts', 'Automation', 'Customer lists']
    },
    {
      icon: Shield,
      title: 'Reviews Management',
      description: 'Build trust with online reviews. We help you get more reviews and respond to customers publicly.',
      details: ['Review requests', 'Response management', 'Review monitoring', 'Reputation tracking']
    },
    {
      icon: Zap,
      title: 'Expert Support',
      description: 'Our team is here to help. Setup, training, updates, and ongoing support - whatever you need, we\'ve got you.',
      details: ['Setup assistance', 'Training calls', 'Priority support', 'Monthly optimization']
    }
  ];

  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <main className="features-page">
      <section className="features-hero">
        <div className="container">
          <h1>Everything You Need to Grow</h1>
          <p>We handle the tech. You focus on your business.</p>
        </div>
      </section>

      <section className="features-grid-section">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={idx}
                  className={`feature-card ${expandedIndex === idx ? 'expanded' : ''}`}
                  onClick={() => setExpandedIndex(expandedIndex === idx ? null : idx)}
                >
                  <div className="feature-header">
                    <div className="feature-icon">
                      <Icon size={32} />
                    </div>
                    <h3>{feature.title}</h3>
                  </div>
                  
                  <p className="feature-description">{feature.description}</p>
                  
                  <div className="feature-details">
                    <ul>
                      {feature.details.map((detail, didx) => (
                        <li key={didx}>{detail}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="expand-hint">
                    {expandedIndex === idx ? '◀' : '▶'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="features-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to grow?</h2>
            <p>All of these features are included in one simple plan.</p>
            <div className="cta-buttons">
              <a href="/plans" className="cta-primary">
                View Plans
              </a>
              <a href="mailto:hello@versalaunch.com" className="cta-secondary">
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="features-faq-preview">
        <div className="container">
          <h2>Common Questions</h2>
          <div className="faq-preview">
            <div className="faq-preview-item">
              <h4>How does the AI phone assistant work?</h4>
              <p>It's an AI receptionist that answers your phone, schedules appointments, answers questions, and even collects payments. Calls are transcribed and logged for you.</p>
            </div>
            <div className="faq-preview-item">
              <h4>Can I use my existing phone number?</h4>
              <p>Yes! We can port your existing number or give you a new professional business number. You choose.</p>
            </div>
            <div className="faq-preview-item">
              <h4>What if I want to make custom changes?</h4>
              <p>We've got a team that can customize almost anything. Let us know what you need.</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Features;
